/**
 * Thread View Multi-Choice Panel — client-side `ThreadMembershipSource` (Track B,
 * group-threading P1b).
 *
 * Adapts the server-mediated membership reader (`threadMembershipRead`, surfaced by
 * `src/repositories/threadMembershipReadClient.ts`) into the panel's
 * `snapshot()`+`canPost()` source, so `membershipThreadQueryPort` derives eligible
 * threads from the REAL participant registry instead of the single-user thread list.
 *
 * Why server-mediated: Firestore rules deny client reads of
 * `prest0n_thread_participants` (server-owned authorization state), so the browser
 * cannot hold the registry directly. The CF already returns ONLY the caller's own
 * threads with their real participant sets — this source caches that projection.
 *
 * Contract (mirrors the canonical `ThreadParticipantRegistry` structurally):
 *   • `snapshot()` is async — it loads once (then serves the warm cache) and projects
 *     the reader rows into `MembershipThreadSnapshotLike`. `membershipThreadQueryPort`
 *     always AWAITS `snapshot()` before calling `canPost()`, and there is no `await`
 *     between them, so the cache cannot change underneath a single query pass.
 *   • `canPost()` is sync + FAIL-CLOSED — true only when the thread is in the warm
 *     cache AND `userId` is one of its participants; an unknown / not-yet-loaded
 *     thread ⇒ false. This is a client-side DISPLAY projection of the server's
 *     participant set; the server gate on the account VM stays the real isolation
 *     boundary — this never authorizes a live turn.
 *   • `refresh()` drops the cache and re-hydrates (fail-closed to empty on error).
 *
 * The fetcher is INJECTED (not imported) so the panel package keeps no dependency on
 * the repositories layer and the source unit-tests with no Firebase / network.
 */
import type {
  MembershipThreadSnapshotLike,
  ThreadMembershipSource,
} from './membershipQueryPort';
import { normalizeCanonicalThreadTitle } from '../groupThreads/canonicalThreadTitle';

/**
 * Minimal row shape this source consumes — structurally satisfied by the reader's
 * `MembershipReadThread` (`src/repositories/threadMembershipReadClient.ts`).
 */
export interface MembershipFetchThread {
  threadId: string;
  workspaceId: string;
  participantIds: string[];
  createdBy?: string;
  createdAt?: number;
  title?: string;
  /** W3b summon setting; only an explicit `false` (a relaxation) is meaningful. */
  requireMentionToSummon?: boolean;
}

/** Injected reader: resolves the caller's group-thread membership (fail-closed → []). */
export type MembershipFetcher = (
  signal?: AbortSignal,
) => Promise<readonly MembershipFetchThread[]>;

/**
 * Outcome of ONE membership read (2026-08-08 tab-parity fix). `ok:true` ⇔ the
 * reader really answered — `rows` are that read's OWN normalized rows
 * (possibly empty). `ok:false` ⇔ the fetch failed; rows are `[]` and say
 * NOTHING about real membership. Atomic per read: a concurrent refresh from
 * another consumer can never make a failed read look answered (or hide a
 * successful one) — the shared cache may belong to a newer generation, but
 * this result never does.
 */
export interface MembershipReadOutcome {
  ok: boolean;
  rows: readonly MembershipThreadSnapshotLike[];
}

export interface ClientMembershipSource extends ThreadMembershipSource {
  /**
   * Force a re-hydrate from the reader; resolves once THIS refresh's own read
   * settles, with that read's atomic outcome (see MembershipReadOutcome).
   * Callers that only await it keep working.
   */
  refresh(signal?: AbortSignal): Promise<MembershipReadOutcome>;
}

/** Project + sanitize a reader row; drop it (→ null) if it has no thread id. */
function normalizeRow(
  row: MembershipFetchThread | null | undefined,
): MembershipThreadSnapshotLike | null {
  const threadId = typeof row?.threadId === 'string' ? row.threadId.trim() : '';
  if (!threadId) return null;
  const participantIds = Array.isArray(row?.participantIds)
    ? row!.participantIds.map((id) => String(id ?? '').trim()).filter(Boolean)
    : [];
  const workspaceId = typeof row?.workspaceId === 'string' ? row.workspaceId.trim() : '';
  const createdBy = typeof row?.createdBy === 'string' ? row.createdBy.trim() || undefined : undefined;
  return {
    threadId,
    workspaceId,
    participantIds,
    createdBy,
    createdAt: Number.isFinite(row?.createdAt as number) ? Number(row!.createdAt) : undefined,
    title: normalizeCanonicalThreadTitle(
      row?.title,
      [threadId, workspaceId, createdBy, ...participantIds],
    ),
    // W3b: carry the stored relaxation through. This row shape is a whitelist,
    // so a field that is not copied here is INVISIBLE to every consumer of the
    // membership source — which is how a persisted "Prest0n replies to every
    // message" silently reverted to "only when mentioned" on reload
    // (INCIDENT 2026-08-08; see the port type for the full trace). Only an
    // explicit false is carried, matching the server projection and the read
    // client: absent means required, everywhere.
    ...(row?.requireMentionToSummon === false ? { requireMentionToSummon: false } : {}),
  };
}

/**
 * Build a cached client membership source over an injected fetcher (normally
 * `fetchThreadMembership`). One in-flight load is de-duplicated; subsequent
 * `snapshot()` calls serve the warm cache until `refresh()`.
 */
export function createClientMembershipSource(
  fetchMembership: MembershipFetcher,
): ClientMembershipSource {
  // threadId → snapshot. A distinct `loaded` flag disambiguates "not loaded yet"
  // from "loaded, zero threads" (both are an empty map).
  let cache = new Map<string, MembershipThreadSnapshotLike>();
  let loaded = false;
  // Whether the most recent cache-owning hydrate came from a REAL reader
  // answer (true) or failed closed to empty (false). Serves warm-cache load()
  // calls; every hydrate additionally reports its OWN outcome — a superseded
  // hydrate must never resolve with this (possibly stale) shared flag.
  let lastHydrateOk = false;
  let inflight: Promise<MembershipReadOutcome> | null = null;
  let generation = 0;
  // FAILED-HYDRATE RE-ATTEMPT (2026-08-21 shared-thread diagnosis): a failed
  // hydrate used to pin `loaded=true` with the empty fail-closed view for the
  // REST OF THE SESSION unless some consumer explicitly refresh()ed — one
  // transient cold-boot miss meant shared threads never surfaced and their
  // transcripts stayed blank (the history floors publish only on a
  // successful read, inside the injected fetcher). Now a load() that finds
  // the cache in the failed state re-hydrates once per backoff window
  // (5s → 10s → … → 60s cap); successes reset the window. Still fail-closed
  // between attempts — a failed view is served, never a stale one.
  let failedAtMs = 0;
  let failRetryDelayMs = 5_000;

  async function hydrate(
    expectedGeneration: number,
    signal?: AbortSignal,
  ): Promise<MembershipReadOutcome> {
    try {
      const rows = (await fetchMembership(signal)) ?? [];
      const next = new Map<string, MembershipThreadSnapshotLike>();
      for (const row of rows) {
        const snap = normalizeRow(row);
        if (snap && !next.has(snap.threadId)) next.set(snap.threadId, snap);
      }
      // A superseded hydrate (a newer refresh() bumped the generation) must
      // not touch the cache — but it still answers for ITSELF.
      if (expectedGeneration === generation) {
        cache = next;
        loaded = true;
        lastHydrateOk = true;
        failRetryDelayMs = 5_000;
      }
      return { ok: true, rows: Array.from(next.values()) };
    } catch {
      if (expectedGeneration === generation) {
        // Fail-closed: any error ⇒ an empty membership view (never a stale/partial one).
        cache = new Map();
        loaded = true;
        lastHydrateOk = false;
        failedAtMs = Date.now();
      }
      return { ok: false, rows: [] };
    }
  }

  function load(signal?: AbortSignal): Promise<MembershipReadOutcome> {
    if (loaded) {
      const failedRetryDue = !lastHydrateOk
        && !inflight
        && Date.now() - failedAtMs >= failRetryDelayMs;
      if (!failedRetryDue) {
        return Promise.resolve({ ok: lastHydrateOk, rows: Array.from(cache.values()) });
      }
      // One bounded re-attempt: widen the window first so a hammering caller
      // can never turn a server outage into a fetch storm.
      failRetryDelayMs = Math.min(60_000, failRetryDelayMs * 2);
      generation += 1;
      loaded = false;
    }
    if (!inflight) {
      const current = hydrate(generation, signal);
      inflight = current;
      void current.finally(() => {
        if (inflight === current) inflight = null;
      });
    }
    return inflight;
  }

  return {
    async snapshot(): Promise<readonly MembershipThreadSnapshotLike[]> {
      await load();
      return Array.from(cache.values());
    },
    canPost(threadId: string, userId: string): boolean {
      const id = (threadId ?? '').trim();
      const actor = (userId ?? '').trim();
      if (!id || !actor) return false;
      const snap = cache.get(id);
      // Fail-closed: unknown / not-yet-hydrated thread ⇒ no post.
      if (!snap) return false;
      return snap.participantIds.includes(actor);
    },
    async refresh(signal?: AbortSignal): Promise<MembershipReadOutcome> {
      generation += 1;
      loaded = false;
      inflight = null;
      return load(signal);
    },
  };
}
